import { useState, useEffect,useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tag,
  Space,
  TreeSelect,
  MessagePlugin
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, ChevronRightIcon, ChevronDownIcon } from 'tdesign-icons-react';
import { menuApi } from '../../api';
import type { Menu } from '../../types';

const menuTypeOptions = [
  { label: '目录', value: 1 },
  { label: '菜单', value: 2 },
  { label: '按钮', value: 3 },
];

export default function MenuManage() {
  const [data, setData] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const formRef = useRef<any>(null);
  // 手动控制展开状态
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // 递归获取所有子节点ID，用于过滤
  const getAllChildIds = (menus: Menu[], ids: number[] = []): number[] => {
    menus.forEach(m => {
      ids.push(m.id);
      if (m.children?.length) {
        getAllChildIds(m.children, ids);
      }
    });
    return ids;
  };

  // 把树形数据展平，用于表格渲染
  const flattenTree = (menus: Menu[], level = 0): (Menu & { level: number })[] => {
    let result: (Menu & { level: number })[] = [];
    menus.forEach(menu => {
      result.push({ ...menu, level });
      if (menu.children?.length) {
        result = result.concat(flattenTree(menu.children, level + 1));
      }
    });
    return result;
  };

  // 根据展开状态过滤数据
  const getVisibleData = () => {
    const allData = flattenTree(data);
    const visibleIds = new Set<number>();
    const collectVisible = (menus: Menu[]) => {
      menus.forEach(menu => {
        visibleIds.add(menu.id);
        if (expandedIds.has(menu.id) && menu.children?.length) {
          collectVisible(menu.children);
        }
      });
    };
    collectVisible(data);
    return allData.filter(item => visibleIds.has(item.id));
  };

  // 切换展开/收起
  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const columns = [
    { colKey: 'id', title: 'ID', width: 80 },
    {
      colKey: 'menuName',
      title: '菜单名称',
      width: 200,
      cell: ({ row }: any) => {
        const hasChildren = row.children?.length > 0;
        const isExpanded = expandedIds.has(row.id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: row.level * 20 }}>
            {hasChildren ? (
              <Button
                theme="default"
                variant="text"
                icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                onClick={() => toggleExpand(row.id)}
                style={{ marginRight: 4 }}
              />
            ) : (
              <span style={{ width: 24 }} />
            )}
            {row.menuName}
          </div>
        );
      },
    },
    { colKey: 'menuCode', title: '菜单编码', width: 150 },
    {
      colKey: 'menuType',
      title: '类型',
      width: 100,
      cell: ({ row }: any) => {
        const typeMap: Record<number, { label: string; theme: string }> = {
          1: { label: '目录', theme: 'primary' },
          2: { label: '菜单', theme: 'success' },
          3: { label: '按钮', theme: 'default' },
        };
        const type = typeMap[row.menuType] || { label: '未知', theme: 'default' };
        return <Tag theme={type.theme as any}>{type.label}</Tag>;
      },
    },
    { colKey: 'path', title: '路由路径', width: 180 },
    { colKey: 'component', title: '组件路径', width: 200, ellipsis: true },
    { colKey: 'permission', title: '权限标识', width: 150 },
    {
      colKey: 'status',
      title: '状态',
      width: 80,
      cell: ({ row }: any) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'}>
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    { colKey: 'sortOrder', title: '排序', width: 80 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            icon={<EditIcon />}
            onClick={() => handleEdit(row)}
          >
            编辑
          </Button>
          <Popconfirm
            content="确定要删除该菜单吗？"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await menuApi.getTree();
      if (res.code === 200) {
        setData(res.data);
        // 默认全部隐藏
        setExpandedIds(new Set());
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  const handleAdd = () => {
    setEditingMenu(null);
    setDialogVisible(true);
    requestAnimationFrame(() => {
      formRef.current?.reset();
      formRef.current?.setFieldsValue({
        parentId: 0,
        status: true,
        sortOrder: 0,
        menuType: 2,
      });
    });
  };

  const handleEdit = (menu: Menu) => {
    setEditingMenu(menu);
    setDialogVisible(true);
    requestAnimationFrame(() => {
      formRef.current?.reset();
      formRef.current?.setFieldsValue({
        parentId: menu.parentId || 0,
        menuName: menu.menuName,
        menuCode: menu.menuCode,
        menuType: menu.menuType,
        icon: menu.icon || '',
        path: menu.path || '',
        component: menu.component || '',
        permission: menu.permission || '',
        status: menu.status === 1,
        sortOrder: menu.sortOrder || 0,
      });
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await menuApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch (err) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    const validateResult = await formRef.current?.validate();
    if (validateResult !== true && Object.keys(validateResult).length > 0) {
      MessagePlugin.warning('请完善表单信息');
      return;
    }
    const values = formRef.current?.getFieldsValue(true);
    const submitData = { ...values, status: values.status ? 1 : 0 };
    try {
      let res;
      if (editingMenu) {
        res = await menuApi.update(editingMenu.id, submitData);
      } else {
        res = await menuApi.create(submitData);
      }
      if (res.code === 200) {
        MessagePlugin.success(editingMenu ? '更新成功' : '创建成功');
        setDialogVisible(false);
        formRef.current?.reset();
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const buildTreeData = (menus: Menu[]): any[] => {
    return menus.map((menu) => ({
      label: menu.menuName,
      value: menu.id,
      children: menu.children?.length ? buildTreeData(menu.children) : undefined,
    }));
  };

  const treeData = [{ label: '顶级菜单', value: 0 }, ...buildTreeData(data)];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">菜单管理</h1>
        <Button theme="primary" icon={<AddIcon />} onClick={handleAdd}>
          新增菜单
        </Button>
      </div>

      <Table
        data={getVisibleData()}
        columns={columns}
        rowKey="id"
        loading={loading}
      />

      <Dialog
        header={editingMenu ? '编辑菜单' : '新增菜单'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false);
          setEditingMenu(null);
          formRef.current?.reset();
        }}
        footer={null}
        width={600}
      >
        <Form ref={formRef}
            initialData={editingMenu || {}} labelWidth={100}>
          <Form.FormItem label="上级菜单" name="parentId" initialValue={0}>
            <TreeSelect data={treeData} placeholder="请选择上级菜单" />
          </Form.FormItem>
          <Form.FormItem 
            label="菜单名称" 
            name="menuName" 
            rules={[{ required: true, message: '请输入菜单名称' }]}
          >
            <Input placeholder="请输入菜单名称" />
          </Form.FormItem>
          <Form.FormItem label="菜单编码" name="menuCode">
            <Input placeholder="请输入菜单编码" />
          </Form.FormItem>
          <Form.FormItem 
            label="菜单类型" 
            name="menuType" 
            rules={[{ required: true, message: '请选择菜单类型' }]} 
            initialValue={2}
          >
            <Select options={menuTypeOptions} placeholder="请选择菜单类型" />
          </Form.FormItem>
          <Form.FormItem label="图标" name="icon">
            <Input placeholder="请输入图标名称" />
          </Form.FormItem>
          <Form.FormItem label="路由路径" name="path">
            <Input placeholder="请输入路由路径" />
          </Form.FormItem>
          <Form.FormItem label="组件路径" name="component">
            <Input placeholder="请输入组件路径" />
          </Form.FormItem>
          <Form.FormItem label="权限标识" name="permission">
            <Input placeholder="请输入权限标识" />
          </Form.FormItem>
          <Form.FormItem label="状态" name="status" initialValue={true}>
            <Switch />
          </Form.FormItem>
          <Form.FormItem label="排序" name="sortOrder" initialValue={0}>
            <Input type="number" placeholder="请输入排序" />
          </Form.FormItem>
        </Form>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button 
            theme="default" 
            variant="outline"
            onClick={() => {
              setDialogVisible(false);
              setEditingMenu(null);
              form.reset();
            }}
          >
            取消
          </Button>
          <Button theme="primary" onClick={handleSubmit}>
            {editingMenu ? '保存修改' : '确认创建'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}